import { all, select, takeEvery, fork, put, call, delay } from 'redux-saga/effects'

declare global {
	interface Window {
		payVerse: any
	}
}

interface PayverseResultType {
	mallReserved: string
	orderId: string
	resultCode: string
	resultMessage: string
	resultStatus: string
	tid: string
}

interface KrwPaymentResultCallbackBodyType {
	mid: string
	clientKey: string
	reqDate: string
	sign: string
	orderId: string
	productName: string
	requestCurrency: string
	requestAmount: string
	returnUrl: string
	webhookUrl: string
}

// action
import { paymentActions, routeActions, shopActions } from '@redux/store/index'

// lib
import {
	Analytics,
	Logger,
	ShippingBox,
	ShippingBoxSizeType,
	Fedex,
	CartQuote,
	CartKey,
	DisplayCurrency,
	checkoutAuthHeaders,
} from '@utils'
import { Validator, ValidatorResultType } from '@validators'
import { produce } from 'immer'
import { AsYouType, CountryCode } from 'libphonenumber-js'
import { Country, ICountry } from 'country-state-city'

// api
import { seasonProductApi, ResType, paymentApi } from '@api'

// schemas
import { SeasonProduct, SeasonProductType, CustomerType } from '@schemas'

// type
import type {
	PaymentStateType,
	CartType,
	PaymentCustomerInfoType,
	PaymentDeliveryType,
	PaymentShippingType,
	PaymentCardType,
	PaymentCostType,
	CheckoutQuoteType,
	CheckoutQuoteProblemType,
	CheckoutCurrency,
} from '@redux/types'

/**
 * 결제 사가 (judahsWeb/CHECKOUT_PLAN.md §5).
 *
 * 금액은 화면이 계산하지 않는다 — `POST /payment/quote` 가 준 견적을 그대로 찍고, 결제 요청에는
 * 장바구니 줄(id · qty · option · 담을 때 본 단가)만 보낸다. 서버가 같은 견적을 다시 내서 청구하므로
 * 화면의 Total 과 게이트웨이 청구액과 인보이스가 한 값이다.
 *
 * 견적이 "지금은 다르다"(가격 변경 · 품절 · 재고 부족 · 쿠폰)고 하면 장바구니를 그 값으로 고치고
 * 'quoteChanged' 모달로 무엇이 바뀌었는지 보여 준 뒤 견적을 다시 받는다. 결제 실행 중 409
 * `[CHECKOUT]:QUOTE-MISMATCH` 가 와도 같은 길을 탄다.
 */
/** 결제 화면의 상품 목록. 고객 토큰을 싣는다 — 판매 전 테스트 허용 고객(PREVIEW_BUYER_EMAILS)이 여기서 풀리지 않으면 안 된다 */
const apiGetSeasonProduct = async (): Promise<ResType | undefined> => {
	const res: ResType | undefined = await seasonProductApi.getShopSeasonProduct({
		customHeaders: checkoutAuthHeaders(),
	})
	return res
}

const apiQuote = async (body: {
	items: any[]
	couponCode: string
	countryIsoCode: string
	cartKey: string
}): Promise<ResType | undefined> => {
	const res: ResType | undefined = await paymentApi.quote({
		body: body,
		customHeaders: checkoutAuthHeaders(),
	})
	return res
}

/** 결제 화면을 여는 순간 — 견적 + 가격 잠금 (CHECKOUT_PLAN §13) */
const apiCheckoutOpen = async (body: {
	items: any[]
	couponCode: string
	countryIsoCode: string
	cartKey: string
}): Promise<ResType | undefined> => {
	const res: ResType | undefined = await paymentApi.checkoutOpen({
		body: body,
		customHeaders: checkoutAuthHeaders(),
	})
	return res
}

const apiCardCredit = async (body: { [key: string]: any }) => {
	const res: ResType | undefined = await paymentApi.cardCredit({
		body: body,
		customHeaders: checkoutAuthHeaders(),
	})
	return res
}

const apiCardIntegrated = async (body: { [key: string]: any }) => {
	const res: ResType | undefined = await paymentApi.cardIntegrated({
		body: body,
		customHeaders: checkoutAuthHeaders(),
	})
	return res
}

/*
	결제가 실패해 방금 만든 임시주문을 되돌린다.

	`cancelToken` 은 임시주문을 만든 응답에만 실려 오는 1회성 표다. 비회원 결제를 받기 때문에
	이 API 에 로그인 가드를 걸 수 없어서, 주문 ID 만 알면 남의 주문이 지워지던 자리를 이 표가
	대신 막는다(judahsWeb/SECURITY.md B1).
*/
/** PayVerse 창 콜백 결과를 서버 원장에 남긴다. 실패해도 흐름을 막지 않는다 */
export const apiCardIntegratedResult = async (body: {
	orderId: string
	cancelToken: string
	resultStatus: string
	resultCode: string
	resultMessage: string
	tid: string
}): Promise<ResType | undefined> => {
	const res: ResType | undefined = await paymentApi.cardIntegratedResult({
		body: body,
		customHeaders: checkoutAuthHeaders(),
	})
	return res
}

/** 주문 확정 여부(서버). KRW 는 웹훅이 확정한다 */
export const apiOrderStatus = async (
	orderId: string,
	cancelToken: string,
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await paymentApi.orderStatus({
		body: { orderId, cancelToken },
		customHeaders: checkoutAuthHeaders(),
	})
	return res
}

export const apiCancelCardOrder = async (orderId: string, cancelToken: string) => {
	const res: ResType | undefined = await paymentApi.cancelCardOrder({
		body: {
			orderId: orderId,
			cancelToken: cancelToken,
		},
		customHeaders: checkoutAuthHeaders(),
	})
	return res
}

const currencyOf = (delivery: PaymentDeliveryType): CheckoutCurrency =>
	delivery.countryIsoCode === 'KR' ? 'KRW' : 'USD'

const saveCart = (cartList: CartType[]) => {
	localStorage.setItem('cart', cartList.length === 0 ? '' : JSON.stringify(cartList))
	window.dispatchEvent(new Event('storage'))
}

const errorTitle = (apiRes: ResType | undefined): string =>
	apiRes?.exception?.message?.meta?.title ?? ''

const errorProblems = (apiRes: ResType | undefined): CheckoutQuoteProblemType[] => {
	const detail: { problems?: CheckoutQuoteProblemType[] } | undefined =
		apiRes?.exception?.message?.meta?.detail
	return detail?.problems ?? []
}

/**
 * 견적을 받아 cost·shipping·quote 를 채운다. `open` 이면 결제 화면을 여는 호출이라 가격도 잠근다.
 *
 * 값이 바뀐 줄은 자동으로 고치지 않는다 (사용자 결정 2026-09-16):
 *   올랐으면 PriceChangeModal 이 "그래도 사겠는가"를 줄마다 묻는다(체크박스) → PRICE_DECISION
 *   내렸으면 새 값으로 바꾸고 알리기만 한다
 * 그 밖의 문제(품절·재고 부족·옵션·판매 불가)는 장바구니를 고치고 'quoteChanged' 모달로 알린 뒤 다시 받는다.
 */
function* quoteSaga(depth = 0, open = false): any {
	try {
		const tryToPayment: PaymentStateType['tryToPayment'] = yield select(
			state => state.payment.tryToPayment,
		)
		const cartList: CartType[] = tryToPayment.cartList
		const delivery: PaymentDeliveryType = yield select(
			state => state.payment.paymentDelivery,
		)
		const shipping: PaymentShippingType = yield select(
			state => state.payment.paymentShipping,
		)
		const cost: PaymentCostType = yield select(state => state.payment.paymentCost)

		if (cartList.length === 0) {
			yield put(paymentActions.QUOTE(null))
			yield put(paymentActions.QUOTE_STATUS('empty'))
			return
		}

		const currency: CheckoutCurrency = currencyOf(delivery)

		yield put(paymentActions.QUOTE_STATUS('loading'))

		const body = {
			items: CartQuote.toQuoteItems(cartList, currency),
			couponCode: cost.couponCode,
			countryIsoCode: delivery.countryIsoCode,
			cartKey: CartKey.get(),
		}
		const apiRes: ResType | undefined = yield call(
			open ? apiCheckoutOpen : apiQuote,
			body,
		)

		// requestManager 는 POST 에 201 을 준다
		if (apiRes === undefined || ![200, 201].includes(apiRes.status)) {
			yield put(paymentActions.QUOTE_STATUS('empty'))
			yield put(
				paymentActions.MODAL_INFO({ target: 'serverError', title: 'Server Error' }),
			)
			yield put(paymentActions.SHOW_MODAL(true))
			return
		}

		const quote = apiRes.data[0] as CheckoutQuoteType

		if (!quote.ok) {
			// 1) 값 변경 — 묻거나 알린다
			const changes = CartQuote.priceChanges(cartList, quote)
			if (changes.increased.length > 0 || changes.decreased.length > 0) {
				yield put(paymentActions.PRICE_CHANGES(changes))
				yield put(paymentActions.QUOTE(quote))
				yield put(paymentActions.QUOTE_STATUS('ok'))

				if (changes.increased.length === 0) {
					// 내린 것만 — 새 값으로 바꾸고 알린다
					const nextCartList = CartQuote.applyPriceDecision(cartList, quote, changes, [])
					saveCart(nextCartList)
					yield put(paymentActions.TRY_TO_PAYMENT({ cartList: nextCartList }))
				}

				yield put(
					paymentActions.MODAL_INFO({
						target: 'priceChanged',
						title: 'The price changed',
					}),
				)
				yield put(paymentActions.SHOW_MODAL(true))

				if (changes.increased.length === 0 && depth < 2) {
					yield call(quoteSaga, depth + 1, open)
				}
				return
			}

			// 2) 그 밖의 문제 — 장바구니를 고치고 알린다
			const fix = CartQuote.applyProblems(cartList, quote)

			if (fix.couponProblems.length > 0) {
				yield put(
					paymentActions.PAYMENT_COUPON_ERROR_MESSAGE(
						CartQuote.describe(fix.couponProblems[0], currency),
					),
				)
				yield put(paymentActions.PAYMENT_COST({ ...cost, couponCode: '' }))
			}

			if (fix.changed) {
				saveCart(fix.cartList)
				yield put(paymentActions.TRY_TO_PAYMENT({ cartList: fix.cartList }))
				yield put(paymentActions.QUOTE_PROBLEMS(fix.lineProblems))
				yield put(
					paymentActions.MODAL_INFO({
						target: 'quoteChanged',
						title: 'The cart changed',
					}),
				)
				yield put(paymentActions.SHOW_MODAL(true))

				if (fix.cartList.length === 0) {
					yield put(paymentActions.QUOTE(null))
					yield put(paymentActions.QUOTE_STATUS('empty'))
					yield put(routeActions.NAVIGATE_PATH('/cart'))
					return
				}
			}

			if ((fix.changed || fix.couponProblems.length > 0) && depth < 2) {
				yield call(quoteSaga, depth + 1, open)
				return
			}
		}

		yield put(paymentActions.QUOTE(quote))
		yield put(paymentActions.QUOTE_STATUS('ok'))

		const newCost: PaymentCostType = {
			totalUsd: quote.subtotalUsd,
			vatUsd: 0,
			shippingUsd: quote.shippingUsd,
			discountedUsd: quote.discountUsd,
			discountedKrw: quote.discountKrw,
			paidUsd: quote.paidUsd,
			couponCode: quote.coupon.applied ? quote.coupon.code : cost.couponCode,
			currency: quote.currency,
			subtotalAmount: quote.subtotalAmount,
			discountAmount: quote.discountAmount,
			shippingAmount: quote.shippingAmount,
			paidAmount: quote.paidAmount,
		}
		yield put(paymentActions.PAYMENT_COST(newCost))

		if (quote.coupon.applied) {
			yield put(paymentActions.PAYMENT_COUPON_ERROR_MESSAGE(''))
		}

		if (shipping.usd !== quote.shippingUsd) {
			yield put(paymentActions.PAYMENT_SHIPPING({ ...shipping, usd: quote.shippingUsd }))
		}
	} catch (error) {
		Logger.warn(error)
		yield put(paymentActions.QUOTE_STATUS('empty'))
	}
}

/** PriceChangeModal 의 결정 — 체크된 오른 줄만 남기고 전부 지금 값으로. 그 뒤 견적을 다시 받는다 */
function* priceDecisionSaga(action: { payload: string[] }): any {
	try {
		const tryToPayment: PaymentStateType['tryToPayment'] = yield select(
			state => state.payment.tryToPayment,
		)
		const quote: CheckoutQuoteType | null = yield select(state => state.payment.quote)
		const changes: PaymentStateType['priceChanges'] = (yield select(
			state => state.payment.priceChanges,
		)) ?? { currency: 'USD', increased: [], decreased: [] }

		if (quote === null) return

		const nextCartList = CartQuote.applyPriceDecision(
			tryToPayment.cartList,
			quote,
			changes,
			action.payload,
		)
		saveCart(nextCartList)
		yield put(paymentActions.TRY_TO_PAYMENT({ cartList: nextCartList }))
		yield put(
			paymentActions.PRICE_CHANGES({
				currency: changes.currency,
				increased: [],
				decreased: [],
			}),
		)

		if (nextCartList.length === 0) {
			yield put(paymentActions.QUOTE(null))
			yield put(paymentActions.QUOTE_STATUS('empty'))
			yield put(routeActions.NAVIGATE_PATH('/cart'))
			return
		}

		yield call(quoteSaga, 0, true)
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerQuoteSaga(): any {
	yield call(quoteSaga, 0)
}

/**
 * 한국에서 온 방문자의 배송국을 미리 채운다 — Shop·Cart 와 같은 판정(PRICING §6, 2026-09-16).
 *
 * 결제 통화는 배송국이 정한다(`currencyOf`). 그래서 국가가 비어 있으면 첫 견적이 USD 로 열리고,
 * 원화만 보던 사람이 결제 화면에서 달러를 본다. 표시 통화가 원화면 KR 을 먼저 넣어 첫 견적부터
 * 원화로 연다. 방문자가 이미 고른 국가는 덮지 않고, 원화 단가가 없는 줄(옛 장바구니)이 하나라도
 * 섞여 있으면 그냥 둔다 — Cart 의 `localKrwOk` 와 같은 조건이다.
 */
function* presetKoreanDeliverySaga(cartList: CartType[]): any {
	const delivery: PaymentDeliveryType = yield select(
		state => state.payment.paymentDelivery,
	)
	if (delivery.countryIsoCode !== '') return
	if (cartList.some(anItem => (anItem.unitKrw ?? 0) <= 0)) return

	// 서버 판정(GET /geo)이 이번 세션에 없으면 한 번 묻고 기다린다. 있으면 그대로 돌아온다
	yield call(DisplayCurrency.detect)
	if (DisplayCurrency.get() !== 'KRW') return

	const korea: ICountry | undefined = Country.getCountryByCode('KR')
	if (korea === undefined) return

	// 아래 quoteSaga(open) 이 KR 로 여는 것과 같다 — 배송지 사가가 같은 국가로 다시 열지 않게 맞춘다
	lastOpenedCountry = korea.isoCode
	yield put(
		paymentActions.PAYMENT_DELIVERY({
			...delivery,
			countryName: korea.name,
			countryIsoCode: korea.isoCode,
		}),
	)
}

function* triggerInitPaymentPageSaga(): any {
	try {
		lastOpenedCountry = null
		yield put(paymentActions.PAYMENT_STATUS('loading'))
		// 지난 시도의 검증 결과는 이 화면의 것이 아니다 — 경고는 Pay now 를 누른 뒤에만 선다
		yield put(paymentActions.RESET_PAYMENT_ERROR_MESSAGES())

		const tryToPayment: PaymentStateType['tryToPayment'] = yield select(
			state => state.payment.tryToPayment,
		)
		const cartList: CartType[] = tryToPayment.cartList

		if (cartList.length === 0) {
			yield put(paymentActions.PAYMENT_STATUS('init'))
			yield put(routeActions.NAVIGATE_PATH('/cart'))
			return
		}

		// 상점 목록은 화면(Items 블록 · 시즌 ID)이 쓴다. 가격·재고 판정은 견적이 한다
		const apiRes: ResType | undefined = yield call(apiGetSeasonProduct)

		if (apiRes === undefined || apiRes.status !== 200) {
			yield put(
				paymentActions.MODAL_INFO({ target: 'serverError', title: 'Server Error' }),
			)
			yield put(paymentActions.SHOW_MODAL(true))
			yield put(paymentActions.PAYMENT_STATUS('init'))
			return
		}

		yield put(shopActions.SEASON_ID(apiRes.data[0].season?.id ?? ''))
		const data = apiRes.data[0].productList as any[]
		const newSeasonProductList: SeasonProductType[] = data.map(
			aData => new SeasonProduct(aData),
		)
		yield put(shopActions.PRODUCT_LIST(newSeasonProductList))

		yield call(presetKoreanDeliverySaga, cartList)
		yield call(shippingCostSaga)
		yield call(quoteSaga, 0, true)

		const afterQuote: PaymentStateType['tryToPayment'] = yield select(
			state => state.payment.tryToPayment,
		)
		if (afterQuote.cartList.length > 0) {
			yield put(paymentActions.PAYMENT_STATUS('ok'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

/** 결제 응답을 성공 화면 값으로 */
function* onPaid(params: {
	summary: { [key: string]: any }
	cartList: CartType[]
	delivery: PaymentDeliveryType
	customerInfo: PaymentCustomerInfoType
	phoneNumber: string
	paymentType: 'paypal' | 'card' | 'card_krw'
	couponCode: string
	/** KRW: 서버 확정을 아직 못 봤다. 성공 화면이 이어서 폴링한다 */
	confirming?: boolean
	cancelToken?: string
}): any {
	const { summary, cartList, delivery, customerInfo } = params
	const confirming: boolean = params.confirming === true
	const orderDetail = cartList
		.map((anItem: CartType) => `${anItem.productName} x ${anItem.qty}`)
		.join(',\n')
	const orderAddress =
		delivery.countryIsoCode === 'KR'
			? `대한민국 ${delivery.stateName} ${delivery.city} \n${delivery.street1} \n${delivery.street2}`
			: [
					delivery.street2,
					delivery.street1,
					delivery.city,
					delivery.countryIsoCode === 'US' ? delivery.stateIsoCode : delivery.stateName,
					delivery.countryName,
			  ].join(' ')

	yield put(
		paymentActions.PAYMENT_SUCCESS({
			orderId: summary.orderId ?? '',
			orderDetail: orderDetail,
			address: orderAddress,
			email: summary.customerEmail || customerInfo.email,
			phoneNumber: params.phoneNumber,
			currency: summary.currency ?? '',
			paidAmount: Number(summary.paidAmount ?? 0),
			confirming,
			cancelToken: confirming ? params.cancelToken ?? '' : '',
		}),
	)

	// 확정을 못 본 주문은 구매 이벤트를 찍지 않는다 — 웹훅이 안 오면 결제가 아니다
	if (!confirming)
		Analytics.purchase({
			transactionId: summary.orderId ?? '',
			cartList: cartList,
			value: Number(summary.paidAmount ?? 0),
			currency: summary.currency ?? 'USD',
			shipping: Number(summary.shippingAmount ?? 0),
			coupon: params.couponCode,
			paymentType: params.paymentType,
		})

	saveCart([])
	yield put(paymentActions.TRIGGER_RESET_PAYMENT_DATA(true))
	yield put(routeActions.NAVIGATE_PATH('/payment-success'))
}

/** 결제 실행이 거절됐을 때 — 견적 불일치면 장바구니를 고치고 다시 견적, 그 밖은 결제 오류 모달 */
function* onPayRejected(apiRes: ResType | undefined): any {
	const title = errorTitle(apiRes)

	if (title === '[CHECKOUT]:QUOTE-MISMATCH') {
		const problems = errorProblems(apiRes)
		yield put(paymentActions.QUOTE_PROBLEMS(problems))
		yield call(quoteSaga, 0)
		// quoteSaga 가 값을 묻거나 장바구니를 고쳤으면 그 모달이 떠 있다. 아니면(예: 쿠폰) 여기서 알린다
		const showing: boolean = yield select(state => state.payment.showModal)
		if (!showing) {
			yield put(
				paymentActions.MODAL_INFO({ target: 'quoteChanged', title: 'The cart changed' }),
			)
			yield put(paymentActions.SHOW_MODAL(true))
		}
		return
	}

	if (
		title.includes('PAYVERSE-PAYMENT-FAILED') ||
		title.includes('PAYVERSE-TRANSACTION-FAILED')
	) {
		yield put(
			paymentActions.MODAL_INFO({ target: 'paymentError', title: 'Payment Error' }),
		)
		yield put(paymentActions.SHOW_MODAL(true))
		return
	}

	yield put(
		paymentActions.MODAL_INFO({ target: 'unexpectedError', title: 'Unexpected error' }),
	)
	yield put(paymentActions.SHOW_MODAL(true))
}

function* triggerUsdPayNowSaga(): any {
	try {
		const tryToPayment: PaymentStateType['tryToPayment'] = yield select(
			state => state.payment.tryToPayment,
		)
		const cartList: CartType[] = tryToPayment.cartList
		const customerInfo: PaymentCustomerInfoType = yield select(
			state => state.payment.paymentCustomerInfo,
		)
		const delivery: PaymentDeliveryType = yield select(
			state => state.payment.paymentDelivery,
		)
		const shipping: PaymentShippingType = yield select(
			state => state.payment.paymentShipping,
		)
		const card: PaymentCardType = yield select(state => state.payment.paymentCard)
		const cost: PaymentCostType = yield select(state => state.payment.paymentCost)
		const errorMessages: PaymentStateType['paymentErrorMessages'] = yield select(
			state => state.payment.paymentErrorMessages,
		)
		const customer: CustomerType = yield select(state => state.customerAuth.customer)
		const customerId: string = customer.id

		const res: ValidatorResultType = Validator.payment({
			givenName: customerInfo.givenName,
			familyName: customerInfo.familyName,
			email: customerInfo.email,
			phoneCountryCode: customerInfo.phoneCountryCode,
			phoneNumber: customerInfo.phoneNumber,

			countryName: delivery.countryName,
			states: delivery.stateName,
			city: delivery.city,
			street1: delivery.street1,
			zip: delivery.zip,

			cardNumber: card.cardNumber,
			expiredMonth: card.expiredMonth,
			expiredYear: card.expiredYear,
			cvc: card.cvc,
			cardName: card.cardName,
		})

		const filterInvalidRes = Object.keys(res).filter(aKey => !res[aKey].isValid)
		const isSuccess = filterInvalidRes.length === 0

		const newErrorMessage = produce(errorMessages, draft => {
			draft.customer.givenName = res['givenName'].message
			draft.customer.familyName = res['familyName'].message
			draft.customer.email = res['email'].message
			draft.customer.phoneCountryCode = res['phoneCountryCode'].message
			draft.customer.phoneNumber = res['phoneNumber'].message

			draft.delivery.countryName = res['countryName'].message
			draft.delivery.states = res['states'].message
			draft.delivery.city = res['city'].message
			draft.delivery.street1 = res['street1'].message
			draft.delivery.zip = res['zip'].message

			draft.card.cardNumber = res['cardNumber'].message
			draft.card.expiredMonth = res['expiredMonth'].message
			draft.card.expiredYear = res['expiredYear'].message
			draft.card.cvc = res['cvc'].message
			draft.card.cardName = res['cardName'].message

			draft.isSuccess = isSuccess
		})

		yield put(paymentActions.PAYMENT_ERROR_MESSAGES({ ...newErrorMessage }))

		if (!isSuccess) return

		const asYouType = new AsYouType()
		const newPhoneNumber = asYouType.input(
			`+${customerInfo.phoneCountryCode}${customerInfo.phoneNumber}`,
		)
		const phoneNumber = asYouType.getNationalNumber()

		const body = {
			items: CartQuote.toQuoteItems(cartList, currencyOf(delivery)),
			customer: { ...customerInfo, phoneNumber: phoneNumber },
			delivery: delivery,
			shipping: shipping,
			card: card,
			cost: { couponCode: cost.couponCode },
			customerId: customerId,
			cartKey: CartKey.get(),
		}

		const apiRes: ResType | undefined = yield call(apiCardCredit, body)

		if (apiRes !== undefined && apiRes.status === 201) {
			yield call(onPaid, {
				summary: apiRes.data[0],
				cartList,
				delivery,
				customerInfo,
				phoneNumber: newPhoneNumber,
				paymentType: 'card',
				couponCode: cost.couponCode,
			})
		} else {
			yield call(onPayRejected, apiRes)
		}
	} catch (error) {
		Logger.warn(error)
	}
}

/**
 * 모바일에서는 PayVerse SDK 가 콜백 대신 **페이지 전체를 PayVerse 로 보냈다가 `returnUrl` 로 되돌린다**
 * (payments.js `isMobileLayout` → `location.href`). 이 사가는 그 순간 죽으므로, 되돌아온 성공 페이지가
 * 이어받을 수 있게 임시주문 표를 탭 저장소에 남긴다 (PaymentSuccess.page 의 복귀 처리).
 */
export const KRW_PENDING_KEY = 'judahs_krw_pending'
export interface KrwPendingType {
	orderId: string
	cancelToken: string
	currency: string
	requestAmount: number
	shippingAmount: number
	customerEmail: string
	phoneNumber: string
	couponCode: string
	at: string
}
export const saveKrwPending = (pending: KrwPendingType) => {
	try {
		sessionStorage.setItem(KRW_PENDING_KEY, JSON.stringify(pending))
	} catch (error) {
		Logger.warn(error)
	}
}
export const readKrwPending = (): KrwPendingType | null => {
	try {
		const raw = sessionStorage.getItem(KRW_PENDING_KEY)
		return raw === null || raw === '' ? null : (JSON.parse(raw) as KrwPendingType)
	} catch (error) {
		return null
	}
}
export const clearKrwPending = () => {
	try {
		sessionStorage.removeItem(KRW_PENDING_KEY)
	} catch (error) {
		Logger.warn(error)
	}
}
/** 카트 비우기 — 성공 페이지(모바일 복귀)도 같은 일을 한다 */
export const clearStoredCart = () => {
	localStorage.setItem('cart', '')
	window.dispatchEvent(new Event('storage'))
}

const krwPaymentResultCallback = (body: KrwPaymentResultCallbackBodyType) => {
	if (!window.payVerse) {
		alert('Card payment error occurred.')
		return
	}

	return new Promise((resolve, reject) => {
		window.payVerse.requestUI(
			{
				...body,
				returnUrl: 'https://judahs.io/payment-success',
				webhookUrl: 'https://api.judahs.io/latest/payment/card-integrated-webhook',
			},
			(payverseResult: PayverseResultType) => {
				resolve(payverseResult)
			},
		)
	})
}

function* triggerKrwPayNowSaga(): any {
	try {
		const tryToPayment: PaymentStateType['tryToPayment'] = yield select(
			state => state.payment.tryToPayment,
		)
		const cartList: CartType[] = tryToPayment.cartList
		const customerInfo: PaymentCustomerInfoType = yield select(
			state => state.payment.paymentCustomerInfo,
		)
		const delivery: PaymentDeliveryType = yield select(
			state => state.payment.paymentDelivery,
		)
		const shipping: PaymentShippingType = yield select(
			state => state.payment.paymentShipping,
		)
		const cost: PaymentCostType = yield select(state => state.payment.paymentCost)
		const errorMessages: PaymentStateType['paymentErrorMessages'] = yield select(
			state => state.payment.paymentErrorMessages,
		)
		const customer: CustomerType = yield select(state => state.customerAuth.customer)
		const customerId: string = customer.id

		const res: ValidatorResultType = Validator.payment({
			givenName: customerInfo.givenName,
			familyName: customerInfo.familyName,
			email: customerInfo.email,
			phoneCountryCode: customerInfo.phoneCountryCode,
			phoneNumber: customerInfo.phoneNumber,

			countryName: delivery.countryName,
			states: delivery.stateName,
			city: delivery.city,
			street1: delivery.street1,
			zip: delivery.zip,
		})

		const filterInvalidRes = Object.keys(res).filter(aKey => !res[aKey].isValid)
		const isSuccess = filterInvalidRes.length === 0

		const newErrorMessage = produce(errorMessages, draft => {
			draft.customer.givenName = res['givenName'].message
			draft.customer.familyName = res['familyName'].message
			draft.customer.email = res['email'].message
			draft.customer.phoneCountryCode = res['phoneCountryCode'].message
			draft.customer.phoneNumber = res['phoneNumber'].message

			draft.delivery.countryName = res['countryName'].message
			draft.delivery.states = res['states'].message
			draft.delivery.city = res['city'].message
			draft.delivery.street1 = res['street1'].message
			draft.delivery.zip = res['zip'].message

			draft.isSuccess = isSuccess
		})

		yield put(paymentActions.PAYMENT_ERROR_MESSAGES({ ...newErrorMessage }))

		if (!isSuccess) return

		const asYouType = new AsYouType(delivery.countryIsoCode.toUpperCase() as CountryCode)
		const newPhoneNumber = asYouType.input(customerInfo.phoneNumber)
		const phoneNumber = asYouType.getNationalNumber()

		const body = {
			items: CartQuote.toQuoteItems(cartList, currencyOf(delivery)),
			customer: { ...customerInfo, phoneNumber: phoneNumber },
			delivery: delivery,
			shipping: shipping,
			cost: { couponCode: cost.couponCode },
			customerId: customerId,
			cartKey: CartKey.get(),
		}

		const apiRes: ResType | undefined = yield call(apiCardIntegrated, body)

		if (apiRes === undefined || apiRes.status !== 201) {
			yield call(onPayRejected, apiRes)
			return
		}

		const data = apiRes.data[0]
		const resBody: KrwPaymentResultCallbackBodyType = {
			mid: data.mid,
			clientKey: data.clientKey,
			reqDate: data.reqDate,
			sign: data.sign,
			orderId: data.orderId,
			productName: data.productName,
			requestCurrency: data.requestCurrency,
			requestAmount: data.requestAmount,
			returnUrl: data.returnUrl,
			webhookUrl: data.webhookUrl,
		}
		const orderId: string = data.orderId
		const cancelToken: string = data.cancelToken || ''
		const quote: CheckoutQuoteType | undefined = data.quote

		saveKrwPending({
			orderId,
			cancelToken,
			currency: resBody.requestCurrency,
			requestAmount: Number(resBody.requestAmount),
			shippingAmount: Number(quote?.shippingAmount ?? 0),
			customerEmail: customerInfo.email,
			phoneNumber: newPhoneNumber,
			couponCode: cost.couponCode,
			at: new Date().toISOString(),
		})

		const payverseResult: PayverseResultType = yield call(
			krwPaymentResultCallback,
			resBody,
		)
		// 데스크톱은 여기로 돌아온다(iframe 콜백). 모바일 복귀는 성공 페이지가 처리하고 그쪽에서 지운다
		clearKrwPending()

		// 창의 결과를 서버 원장에 남긴다 — 실패 이유가 브라우저에만 남던 것을 막는다 (PAYMENT §6.1)
		yield call(apiCardIntegratedResult, {
			orderId,
			cancelToken,
			resultStatus: payverseResult?.resultStatus ?? '',
			resultCode: payverseResult?.resultCode ?? '',
			resultMessage: payverseResult?.resultMessage ?? '',
			tid: payverseResult?.tid ?? '',
		})

		if (payverseResult.resultStatus === 'SUCCESS') {
			/*
				브라우저 콜백은 성공이지만 주문 확정은 서버가 웹훅을 받아야 한다. 몇 초 폴링해서 확정을
				보면 서버 값으로 성공 화면, 못 보면 "확인 중"으로 넘기고 성공 화면이 이어서 폴링한다.
			*/
			let confirmed: { [key: string]: any } | undefined = undefined
			for (let attempt = 0; attempt < 10; attempt += 1) {
				const statusRes: ResType | undefined = yield call(
					apiOrderStatus,
					orderId,
					cancelToken,
				)
				const status = statusRes?.status === 201 ? statusRes.data?.[0] : undefined
				if (status?.paid === true) {
					confirmed = status
					break
				}
				yield delay(2000)
			}

			// 금액·통화는 PayVerse 에 실제로 요청한 값이다 (서명이 묶은 값). 확정을 봤으면 서버 값
			yield call(onPaid, {
				summary: {
					orderId: orderId,
					currency: confirmed?.currency ?? resBody.requestCurrency,
					paidAmount: Number(confirmed?.paidAmount ?? resBody.requestAmount),
					shippingAmount: Number(confirmed?.shippingAmount ?? quote?.shippingAmount ?? 0),
					customerEmail: confirmed?.customerEmail || customerInfo.email,
				},
				cartList,
				delivery,
				customerInfo,
				phoneNumber: newPhoneNumber,
				paymentType: 'card_krw',
				couponCode: cost.couponCode,
				confirming: confirmed === undefined,
				cancelToken,
			})
		} else {
			yield call(apiCancelCardOrder, orderId, cancelToken)
			const reason: string = [payverseResult?.resultCode, payverseResult?.resultMessage]
				.filter(aPart => aPart !== undefined && aPart !== null && String(aPart) !== '')
				.join(' · ')
			yield put(
				paymentActions.MODAL_INFO({
					target: 'paymentError',
					title: 'Payment Error',
					value: reason,
				}),
			)
			yield put(paymentActions.SHOW_MODAL(true))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

/** 쿠폰 적용 = 코드가 실린 견적을 다시 받는 것. 유효하지 않으면 견적이 문제를 돌려준다 */
function* triggerCouponApplySaga(): any {
	try {
		yield put(paymentActions.PAYMENT_COUPON_ERROR_MESSAGE(''))
		yield call(quoteSaga, 0)
	} catch (error) {
		Logger.warn(error)
	}
}

/**
 * 배송사·상자 크기. 배송비(usd)는 견적이 정한다 — 여기 값은 견적이 덮어쓴다.
 * (요율표 규칙은 서버 CheckoutQuoteService 와 같다: KR 무료, 그 밖은 품목 소계 $100 미만이면 $20)
 */
function* shippingCostSaga(): any {
	try {
		const tryToPayment: PaymentStateType['tryToPayment'] = yield select(
			state => state.payment.tryToPayment,
		)
		const delivery: PaymentDeliveryType = yield select(
			state => state.payment.paymentDelivery,
		)
		const cartList: CartType[] = tryToPayment.cartList

		const filterKdm6 = cartList.filter(aCart => aCart.productCode === 'KDM6')
		const filterKdm4 = cartList.filter(aCart => aCart.productCode === 'KDM4')
		const kdm6Qty = filterKdm6.length === 0 ? 0 : filterKdm6[0].qty
		const kdm4Qty = filterKdm4.length === 0 ? 0 : filterKdm4[0].qty
		const size: ShippingBoxSizeType = ShippingBox.getSize({
			kdm6Qty: kdm6Qty,
			kdm4Qty: kdm4Qty,
		})

		if (delivery.countryIsoCode === 'KR') {
			yield put(
				paymentActions.PAYMENT_SHIPPING({
					companyName: 'local',
					deliveryOption: '',
					usd: 0,
					deliveryDays: 0,
					boxType: size.boxType,
					rateType: '',
				}),
			)
			yield put(paymentActions.SHIPPING_COST_LIST([]))
		} else if (
			cartList.length > 0 &&
			delivery.countryIsoCode !== '' &&
			delivery.zip !== '' &&
			delivery.stateIsoCode !== '' &&
			delivery.city !== ''
		) {
			yield put(paymentActions.SHIPPING_COST_STATUS('loading'))

			const { zone } = Fedex.getShippingCost({
				countryCode: delivery.countryIsoCode,
				stateCode: delivery.stateIsoCode,
				zip: delivery.zip,
				width_cm: size.width_cm,
				height_cm: size.height_cm,
				depth_cm: size.depth_cm,
			})
			const isFicp = zone.isAvailable ? 1 : 0
			const subtotalUsd = cartList.reduce(
				(sum, anItem) => sum + anItem.seasonUsd * anItem.qty,
				0,
			)

			yield put(
				paymentActions.PAYMENT_SHIPPING({
					companyName: 'fedex',
					deliveryOption: '',
					usd: subtotalUsd < 100 ? 20 : 0,
					deliveryDays: 0,
					boxType: size.boxType,
					rateType:
						isFicp === 1 ? 'FEDEX_INTERNATIONAL_CONNECT_PLUS' : 'INTERNATIONAL_ECONOMY',
				}),
			)
			yield put(paymentActions.SHIPPING_COST_STATUS('ok'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerGetShippingCostSaga(): any {
	yield call(shippingCostSaga)
}

function* triggerResetPaymentDataSaga(): any {
	try {
		yield put(paymentActions.TRY_TO_PAYMENT({ cartList: [] }))
		yield put(paymentActions.QUOTE(null))
		yield put(paymentActions.QUOTE_STATUS('init'))
		yield put(paymentActions.QUOTE_PROBLEMS([]))
		yield put(
			paymentActions.PRICE_CHANGES({ currency: 'USD', increased: [], decreased: [] }),
		)

		yield put(
			paymentActions.PAYMENT_CUSTOMER_INFO({
				givenName: '',
				familyName: '',
				email: '',
				phoneCountryCode: '',
				phoneNumber: '',
			}),
		)

		yield put(
			paymentActions.PAYMENT_DELIVERY({
				countryName: '',
				countryIsoCode: '',
				stateName: '',
				stateIsoCode: '',
				city: '',
				street1: '',
				street2: '',
				zip: '',
			}),
		)

		yield put(
			paymentActions.PAYMENT_SHIPPING({
				companyName: '',
				deliveryOption: '',
				usd: 0,
				deliveryDays: 0,
				boxType: 'A',
				rateType: '',
			}),
		)

		yield put(
			paymentActions.PAYMENT_COST({
				totalUsd: 0,
				vatUsd: 0,
				shippingUsd: 0,
				discountedUsd: 0,
				discountedKrw: 0,
				paidUsd: 0,
				couponCode: '',
				currency: 'USD',
				subtotalAmount: 0,
				discountAmount: 0,
				shippingAmount: 0,
				paidAmount: 0,
			}),
		)
	} catch (error) {
		Logger.warn(error)
	}
}

function* tryToPaymentSaga(): any {
	try {
		const tryToPayment: PaymentStateType['tryToPayment'] = yield select(
			state => state.payment.tryToPayment,
		)

		// 견적이 장바구니를 고치며 같은 액션을 다시 쏜다 — 이미 결제 화면이면 옮기지 않는다
		if (tryToPayment.cartList.length > 0 && window.location.pathname !== '/payment') {
			yield put(routeActions.NAVIGATE_PATH('/payment'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

/** 배송지가 바뀌면 배송사 → 견적 순으로 다시. 통화(KR ↔ 그 밖)도 여기서 갈린다 */
/*
	배송 정보가 바뀔 때. 전에는 주소 한 글자마다 견적·값 잠금(checkout-open)을 다시 열어 분당 20회씩 나갔다
	(2026-09-19 운영 로그). 견적 통화·배송비를 바꾸는 건 **국가**뿐이므로 국가가 바뀔 때만 다시 연다.
*/
let lastOpenedCountry: string | null = null

function* paymentDeliverySaga(): any {
	try {
		const status: string = yield select(state => state.payment.paymentStatus)
		if (status !== 'ok') return

		const delivery: PaymentDeliveryType = yield select(
			state => state.payment.paymentDelivery,
		)
		if (lastOpenedCountry === delivery.countryIsoCode) return
		lastOpenedCountry = delivery.countryIsoCode

		yield call(shippingCostSaga)
		yield call(quoteSaga, 0, true)
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	yield takeEvery(paymentActions.TRIGGER_INIT_PAYMENT_PAGE, triggerInitPaymentPageSaga)
	yield takeEvery(paymentActions.TRIGGER_USD_PAY_NOW, triggerUsdPayNowSaga)
	yield takeEvery(paymentActions.TRIGGER_KRW_PAY_NOW, triggerKrwPayNowSaga)
	yield takeEvery(paymentActions.TRIGGER_COUPON_APPLY, triggerCouponApplySaga)
	yield takeEvery(paymentActions.TRIGGER_GET_SHIPPING_COST, triggerGetShippingCostSaga)
	yield takeEvery(paymentActions.TRIGGER_RESET_PAYMENT_DATA, triggerResetPaymentDataSaga)
	yield takeEvery(paymentActions.TRIGGER_QUOTE, triggerQuoteSaga)
	yield takeEvery(paymentActions.PRICE_DECISION, priceDecisionSaga)

	yield takeEvery(paymentActions.TRY_TO_PAYMENT, tryToPaymentSaga)

	yield takeEvery(paymentActions.PAYMENT_DELIVERY, paymentDeliverySaga)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
